<template>
  <v-avatar
    :size="size"
    :style="[borderStyle, { backgroundColor: avatarColor }]"
    :class="[
      'ma-1',
      { 'cursor-pointer': user && user.can_change_image },
      user && !user.icon ? 'text-white font-weight-medium' : '',
    ]"
    @click="editImage()"
  >
    <template v-if="user && user.icon">
      <img :src="user.icon" :alt="user.name" />
    </template>
    <template v-else-if="user && (user.short || user.first_name)">
      <span class="text-subtitle-2">{{ userInitial }}</span>
    </template>
    <template v-else>
      <v-icon color="white">mdi-account</v-icon>
    </template>
  </v-avatar>
</template>

<script setup>
import { computed } from "vue";
import { useFormsStore } from "@/stores/formsStore";

const props = defineProps({
  user: {
    type: Object,
    default: null,
  },
  special: {
    type: Boolean,
    default: false,
  },
  size: {
    type: [Number, String],
    default: 40,
  },
});

const formsStore = useFormsStore();

// Predefined colors for avatars (Vuetify material colors)
const colors = [
  "#1976D2", // blue darken-2
  "#00897B", // teal darken-1
  "#7CB342", // light-green darken-1
  "#FFB300", // amber darken-1
  "#F4511E", // deep-orange darken-2
  "#5E35B1", // deep-purple darken-1
  "#00ACC1", // cyan darken-1
  "#43A047", // green darken-1
  "#FB8C00", // orange darken-1
  "#E91E63", // pink
];

const userInitial = computed(() => {
  if (!props.user) return "?";
  if (props.user.short) return props.user.short.toUpperCase();
  if (props.user.first_name)
    return props.user.first_name.charAt(0).toUpperCase();
  if (props.user.name) return props.user.name.charAt(0).toUpperCase();
  return "?";
});

const avatarColor = computed(() => {
  if (!props.user || !userInitial.value) return colors[0];
  // Use the sum of character codes to determine the color
  const charSum = userInitial.value
    .split("")
    .reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return colors[charSum % colors.length];
});

const borderStyle = computed(() => {
  if (props.special && props.user) {
    return "border:3px solid #ffbf00; box-shadow: 0 0 10px 5px #ffbf00;";
  }
  return "";
});

const editImage = () => {
  if (!props.user) return;

  formsStore.edit({
    type: "image",
    item: {
      id: props.user.icon_id || null,
      can_change_image: props.user.can_change_image,
      // Use the larger image (icon_lg) for the pop-up display, fallback to icon
      file: props.user.icon_lg || props.user.icon || null,
      file_dl: props.user.icon_dl || null,
      recordId: props.user.id,
    },
    options: {
      filetype: "image/png, image/jpeg",
      recordType: "User",
      field: "profile_image",
    },
  });
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
